// @flow strict
import type {
  ObjectWritebackFieldMeta,
  WritebackFieldProperty,
  WritebackEntityType,
} from 'src/types/object-writeback';
import type {NodeSchedule} from 'src/types/node-schedule';
import cloneDeep from 'lodash/cloneDeep';
import {getDefaultNodeScheduleState} from 'src/utils/node-schedule';
import {
  RECEIVE_WRITEBACK_ENTITY_TYPES,
  RESET_OBJECT_WRITEBACK,
  SET_BULK_FIELD_PROPERTY,
  ADD_FIELDS_TO_WRITEBACK_FIELD_MAPPING,
  REMOVE_FIELDS_FROM_WRITEBACK_FIELD_MAPPING,
  SET_OBJECT_WRITEBACK_ENTITY_ID,
  RECEIVE_OBJECT_WRITEBACK_INFO,
  UPDATE_OBJECT_WRITEBACK,
  UPDATE_NODE_SCHEDULE,
  UPDATE_OBJECT_WRITEBACK_FIELD,
  INITIALIZE_WRITEBACK_FIELDS,
  SET_WRITEBACK_FIELD_META,
  type ObjectWritebackAction,
} from 'src/action-creators/object-writeback';


export type ObjectWritebackState = {
  writebackEntityTypes: Array<WritebackEntityType>,
  entityObjectId: string,
  writebackFieldMapping: {[string]: WritebackFieldProperty},
  writebackFieldMeta: Array<ObjectWritebackFieldMeta>,
  ...NodeSchedule,
};

const initialState: ObjectWritebackState = {
  writebackEntityTypes: [],
  entityObjectId: '',
  writebackFieldMapping: {},
  writebackFieldMeta: [],
  ...getDefaultNodeScheduleState({}),
};

export default (
  state: ObjectWritebackState = initialState,
  action: ObjectWritebackAction,
): ObjectWritebackState => {
  switch (action.type) {
    case RECEIVE_WRITEBACK_ENTITY_TYPES: {
      return {
        ...state,
        writebackEntityTypes: action.payload,
      };
    }
    case SET_OBJECT_WRITEBACK_ENTITY_ID: {
      if (action.payload !== state.entityObjectId) {
        return {
          ...state,
          entityObjectId: action.payload,
          writebackFieldMapping: {},
        };
      }
      return state;
    }
    case INITIALIZE_WRITEBACK_FIELDS: {
      const {allFields, requiredFieldMapping} = action.payload;
      return {
        ...state,
        writebackFieldMeta: allFields,
        writebackFieldMapping: requiredFieldMapping,
      };
    }
    case SET_WRITEBACK_FIELD_META: {
      return {
        ...state,
        writebackFieldMeta: action.payload,
      };
    }
    case ADD_FIELDS_TO_WRITEBACK_FIELD_MAPPING: {
      return {
        ...state,
        writebackFieldMapping: {
          ...state.writebackFieldMapping,
          ...action.payload,
        },
      };
    }
    case REMOVE_FIELDS_FROM_WRITEBACK_FIELD_MAPPING: {
      const newMapping = {...state.writebackFieldMapping};
      delete newMapping[action.payload];
      return {
        ...state,
        writebackFieldMapping: newMapping,
      };
    }

    case UPDATE_OBJECT_WRITEBACK_FIELD: {
      const {fieldId, updates} = action.payload;
      return {
        ...state,
        writebackFieldMapping: {
          ...state.writebackFieldMapping,
          [fieldId]: {
            ...state.writebackFieldMapping[fieldId],
            isValidChangeType: true,
            isValidValue: true,
            validationMessage: '',
            ...updates,
          },
        },
      };
    }
    case SET_BULK_FIELD_PROPERTY: {
      return {
        ...state,
        writebackFieldMapping: {
          ...state.writebackFieldMapping,
          ...Object.fromEntries(
            Object.entries(action.payload).map(([key, value]) => [
              key,
              {
                ...(state.writebackFieldMapping[key] || {}),
                ...value,
              },
            ]),
          ),
        },
      };
    }
    case RECEIVE_OBJECT_WRITEBACK_INFO: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case RESET_OBJECT_WRITEBACK: {
      const payload = cloneDeep(initialState);
      return {
        ...state,
        ...payload,
      };
    }

    case UPDATE_OBJECT_WRITEBACK: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case UPDATE_NODE_SCHEDULE: {
      return {
        ...state,
        ...action.payload,
      };
    }
  }
  return state;
};
